import React, { Component, ErrorInfo, ReactNode } from "react";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Auto-recover from DOM mutation errors caused by browser extensions
    if (error?.name === 'NotFoundError' && error?.message?.includes('removeChild')) {
      console.warn("Suppressed DOM mutation error (likely browser extension):", error.message);
      return { hasError: false };
    }
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Silently recover from DOM mutation errors
    if (error?.name === 'NotFoundError' && error?.message?.includes('removeChild')) {
      this.setState({ hasError: false });
      return;
    }

    // Report to Sentry
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });

    console.error("ErrorBoundary caught:", error.message, errorInfo.componentStack);

    // Silent auto-reload with 5s throttle to prevent infinite loops
    const lastReload = sessionStorage.getItem('eb_last_reload');
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload) > 5000) {
      sessionStorage.setItem('eb_last_reload', String(now));
      window.location.reload();
    }
    // If throttled, just stay on current page silently
  }

  public render() {
    if (this.state.hasError) {
      // Render nothing while reload happens
      return null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
